import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { ProductView } from '../../api/generated/models/ProductView';
import { applyCouponDiscount, AppliedCoupon } from '../../utils/couponReasons';
import { SubscriptionService } from '../../api/generated/services/SubscriptionService';
import { addNotification } from '../../store/slices/notificationSlice';
import { fetchUserLicenses } from '../../store/slices/licenseSlice';
import type { AppDispatch } from '../../store/store';

import licenseCheckIcon from '../../assets/icons/license-check.svg';
import licenseCrossIcon from '../../assets/icons/license-cross.svg';
import licenseStarIcon from '../../assets/icons/license-star.svg';
import { helper } from '../../utils/helper';
import Hint from '../ui/Hint';
import LicenseCancelModal from './LicenseCancelModal';
import LicenseCancelScheduledChangeModal from './LicenseCancelScheduledChangeModal';

/** Rough tokens per one full product generation; UI estimate only (not from API). */
const CONTENT_MASTER_APPROX_TOKENS_PER_PRODUCT = 2500;
/** Trial plans charge a flat rate per AI operation (per product description); UI estimate only. */
const CONTENT_MASTER_TRIAL_TOKENS_PER_PRODUCT = 5000;

interface LicenseBuyCardProps {
  product: ProductView;
  licenseType: string;
  billingPeriod: 'yearly' | 'monthly';
  onPurchase: (product: ProductView, emailExtraCount?: number, agentExtraCount?: number, totalPriceCents?: number) => void;
  hasActiveLicense?: boolean;
  className?: string;
  activeLicenseId?: string;
  isPaymentPending?: boolean;
  /**
   * True while a change-plan request is in flight (request + 3DS popup /
   * polling). Blocks purchase / switch / change-options actions on every card,
   * mirroring `isPaymentPending` during a regular purchase.
   */
  isChangePlanInProgress?: boolean;
  isCancelled?: boolean;
  expirationDate?: string | null;
  /**
   * Minimum `token_included_count` across the currently displayed content_master
   * products. Used to render the `Nx More Credits` badge relative to the cheapest plan.
   */
  minTokenIncludedCount?: number;
  /**
   * True when the user already has an eligible active subscription (Stripe,
   * isActive, !isCancelled) for THIS plugin on a different product. When true
   * and this card is not the active one and not free, the "Select" CTA is
   * replaced with "Switch to this plan" which delegates to `onChangePlan`.
   */
  pluginCanChangePlan?: boolean;
  /**
   * Display name of the pre-created Subscription B (the pending downgrade
   * target). When defined and this card IS the active one, a badge + "Cancel
   * scheduled change" button are rendered above the Cancel button.
   */
  pendingScheduledProductName?: string;
  /** True when the current user is a subuser - hides Cancel/Change/Cancel-scheduled buttons. */
  isSubuser?: boolean;
  /**
   * Coupon validated via GET /coupon/validate for the current category.
   * When set, the card price is shown crossed out with the discounted price
   * next to it - a UI hint only, Stripe computes the real price.
   */
  couponDiscount?: AppliedCoupon | null;
  /**
   * Freshdesk only: when true, this is the ACTIVE card and the user is allowed
   * to change add-on options (extra emails / agents) on the same product.
   * Unlocks the counters and shows a "Change plan" button.
   */
  allowOptionsChange?: boolean;
  /**
   * Callback fired when the user clicks "Switch to this plan" (non-active card)
   * or "Change plan" (active Freshdesk card). Carries optional Freshdesk add-on
   * counts - only when the user explicitly changed a counter (otherwise
   * undefined = keep the subscription's current value). Parent owns the modal.
   */
  onChangePlan?: (
    targetProduct: ProductView,
    emailExtraCount?: number,
    agentExtraCount?: number,
  ) => void;
  /**
   * Expiry date of the user's active TRIAL subscription when it points at
   * THIS product. Renders a trial note above the buy CTA (warning style when
   * <= 3 days remain). The card still shows the regular buy button - trial
   * upgrades go through the normal checkout flow.
   */
  trialExpirationDate?: string | null;
}

const LicenseBuyCard: React.FC<LicenseBuyCardProps> = ({
  product,
  licenseType,
  billingPeriod,
  onPurchase,
  hasActiveLicense = false,
  className = '',
  activeLicenseId,
  isPaymentPending = false,
  isChangePlanInProgress = false,
  isCancelled = false,
  expirationDate,
  minTokenIncludedCount,
  pluginCanChangePlan = false,
  pendingScheduledProductName,
  isSubuser = false,
  allowOptionsChange = false,
  couponDiscount,
  onChangePlan,
  trialExpirationDate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isUserAgreedWithOverage, setIsUserAgreedWithOverage] = useState(false);
  // State for freshdesk additional options counters
  const [additionalEmails, setAdditionalEmails] = useState(0);
  const [additionalUsers, setAdditionalUsers] = useState(0);
  // Track whether the user explicitly touched each counter. Used so a
  // change-plan request only sends a count the user actually changed -
  // an untouched counter stays `undefined` = "keep current" per the backend
  // contract (avoids silently zeroing existing add-ons).
  const [emailsTouched, setEmailsTouched] = useState(false);
  const [usersTouched, setUsersTouched] = useState(false);
  // State for cancel modal
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  // State for cancel scheduled change modal
  const [isCancelScheduledModalOpen, setIsCancelScheduledModalOpen] = useState(false);
  // State for free license activation
  const [isActivating, setIsActivating] = useState(false);

  // Calculate duration in months from seconds
  const durationMonths = Math.round(product.durationSec / (30 * 24 * 60 * 60));
  const isFree = product.priceCents === 0;
  const isYearly = durationMonths >= 12;
  // Enterprise / "contact sales" plans: cannot be purchased online (backend
  // also rejects payment for them). We hide the price + buy CTA and surface a
  // "Contact sales" button instead.
  const isContactOnly = product.isContactOnly === true;

  // Get options from product and parse string values to numbers
  const options = product.options || {};
  const emailQuotaBase = options.email_quota_base?.value ? parseInt(options.email_quota_base.value, 10) : undefined;
  const emailBlockPriceCents = options.email_block_price_cents?.value ? parseInt(options.email_block_price_cents.value, 10) : undefined;
  const extraUserPriceCents = options.agent_price_cents?.value ? parseInt(options.agent_price_cents.value, 10) : undefined;
  const extraUsersIncluded = options.extra_users_included?.value ? parseInt(options.extra_users_included.value, 10) : undefined;
  const maxUsers = options.max_users?.value ? parseInt(options.max_users.value, 10) : undefined;
  // Mobile app specific options
  const userIncludedCount = options.user_included_count?.value ? parseInt(options.user_included_count.value, 10) : undefined;
  const shopIncludedCount = options.shop_included_count?.value ? parseInt(options.shop_included_count.value, 10) : undefined;
  const syncIntervalRaw = options.sync_interval?.value;
  const syncIntervalSec =
    syncIntervalRaw !== undefined && syncIntervalRaw !== null && String(syncIntervalRaw).trim() !== ''
      ? parseInt(String(syncIntervalRaw).trim(), 10)
      : undefined;
  const jtlWawiEnabled = options.jtl_wawi_enabled?.value === 'true';
  const shippingOrdersEnabled = options.shipping_orders_enabled?.value === 'true';
  const analyticsLevel = options.analytics_level?.value;
  const analyticsEnabled = analyticsLevel !== undefined && analyticsLevel !== 'none' && analyticsLevel !== '';
  const downtimeAlertEnabled = options.downtime_alert?.value === 'true';
  const tokenIncludedRaw = options.token_included_count?.value;
  const tokenIncludedCount =
    tokenIncludedRaw !== undefined && tokenIncludedRaw !== null && String(tokenIncludedRaw).trim() !== ''
      ? parseInt(String(tokenIncludedRaw).trim(), 10)
      : NaN;
  // Content master specific option: whether the plan can edit Wawi attributes.
  const editWawiAttributesEnabled = options.edit_wawi_attributes?.value === 'true';
  const bulkGenerationIncluded = options.bulk_generation_included?.value === 'true';
  // Trial plan flag (content_master): drives per-operation token cost, credits
  // label without "/mo." and the trial-end date on the activate button.
  const isTrial = options.is_trial?.value === 'true';

  // Plan tier derived from the product name for mobile cards.
  // Strict screenshot match: "Seller2Go Free" -> "free", "Seller2Go Pro Monthly" -> "pro", etc.
  const mobilePlanTier: 'free' | 'pro' | 'ultimate' | 'other' = (() => {
    if (licenseType !== 'mobile') return 'other';
    if (isFree) return 'free';
    if (/ultimate/i.test(product.name)) return 'ultimate';
    if (/pro/i.test(product.name)) return 'pro';
    return 'other';
  })();

  // Plan tier for content_master derived from the product name.
  // E.g. "Content Master Starter Monthly (30% off promo)" -> "starter".
  const contentMasterPlanTier: 'starter' | 'pro' | 'ultimate' | 'other' = (() => {
    if (licenseType !== 'content_master') return 'other';
    if (/ultimate/i.test(product.name)) return 'ultimate';
    if (/\bpro\b/i.test(product.name)) return 'pro';
    if (/starter/i.test(product.name)) return 'starter';
    return 'other';
  })();

  // Clean plan name shown on the card title and Select button (e.g. "Pro", "Ultimate").
  const displayName = (() => {
    if (licenseType === 'mobile') {
      const stripped = product.name
        .replace(/^Seller2Go\s+/i, '')
        .replace(/\s+(Monthly|Yearly)\s*$/i, '')
        .trim();
      return stripped || product.name;
    }
    if (licenseType === 'content_master') {
      const stripped = product.name
        .replace(/^Content\s+Master\s+/i, '')
        .replace(/\s*\([^)]*\)\s*$/i, '')
        .replace(/\s+(Monthly|Yearly)\s*$/i, '')
        .trim();
      return stripped || product.name;
    }
    return product.name;
  })();

  // Multiplier used for the "Nx More Credits" badge on content_master cards.
  // Computed strictly from the minimum token_included_count across the displayed products.
  const tokenMultiplier = (() => {
    if (licenseType !== 'content_master') return 1;
    if (!Number.isFinite(tokenIncludedCount) || tokenIncludedCount <= 0) return 1;
    if (!minTokenIncludedCount || minTokenIncludedCount <= 0) return 1;
    const ratio = Math.round(tokenIncludedCount / minTokenIncludedCount);
    return ratio > 1 ? ratio : 1;
  })();

  // True when the additional-features section should use the blue "Everything in X, plus:" style.
  const isTierUpgradeSection =
    (licenseType === 'mobile' && mobilePlanTier !== 'free' && mobilePlanTier !== 'other') ||
    (licenseType === 'content_master' && (contentMasterPlanTier === 'pro' || contentMasterPlanTier === 'ultimate'));

  const getTotalPriceCents = (): number => {
    if (isFree) return 0;
    
    let totalAmount = product.priceCents;
    
    // Add additional costs for freshdesk
    if (licenseType === 'freshdesk' && !isFree) {
      const additionalEmailCost = additionalEmails * (emailBlockPriceCents || 0);
      const additionalUserCost = additionalUsers * (extraUserPriceCents || 0);
      totalAmount += additionalEmailCost + additionalUserCost;
    }
    
    return totalAmount;
  };

  // Coupon (validated via GET /coupon/validate): the displayed price is
  // recalculated locally as a UI hint; Stripe computes the real price.
  const hasCouponDiscount = !!couponDiscount && !isFree && !isContactOnly;

  const getEffectiveTotalPriceCents = (): number => {
    const total = getTotalPriceCents();
    if (!hasCouponDiscount) return total;
    return applyCouponDiscount(total, couponDiscount);
  };

  const getCardPrice = () => {
    return helper.formatPrice({ amount: getEffectiveTotalPriceCents(), currency: 'EUR' });
  };

  // Yearly paid plans display the monthly equivalent (yearly sum / 12) in the
  // card header; the button / checkout still show the full charged sum.
  const showMonthlyEquivalent = isYearly && !isFree && !isContactOnly;

  const getDisplayPriceCents = (cents: number): number =>
    showMonthlyEquivalent ? cents / 12 : cents;

  const getHeaderPrice = () => {
    return helper.formatPrice({ amount: getDisplayPriceCents(getEffectiveTotalPriceCents()), currency: 'EUR' });
  };

  // E.g. "Content Master Starter Monthly (30% off promo)" -> 30.
  const promoDiscountPercent = (() => {
    const match = product.name.match(/(\d+)%\s*off/i);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return Number.isFinite(parsed) && parsed > 0 && parsed < 100 ? parsed : null;
  })();

  const getOriginalPriceCents = (): number | null => {
    if (product.priceCents <= 0) return null;

    if (promoDiscountPercent !== null) {
      const discountMultiplier = 1 - promoDiscountPercent / 100;
      const prePromoPriceCents = product.priceCents / discountMultiplier;

      if (isYearly && billingPeriod === 'yearly') {
        // Yearly plans charge for 10 months; show 12-month equivalent before promo.
        return prePromoPriceCents / 10 * 12;
      }

      if (!isYearly && billingPeriod === 'monthly') {
        return prePromoPriceCents;
      }

      return null;
    }

    if (isYearly && billingPeriod === 'yearly') {
      return product.priceCents / 10 * 12;
    }
    return null;
  };

  const getOriginalPrice = () => {
    const originalCents = getOriginalPriceCents();
    if (originalCents === null) return null;
    return helper.formatPrice({ amount: getDisplayPriceCents(originalCents), currency: 'EUR' });
  };

  const getDiscountPercent = (): number | null => {
    const originalCents = getOriginalPriceCents();
    if (originalCents === null || originalCents <= 0) return null;
    const percent = Math.round((1 - product.priceCents / originalCents) * 100);
    return percent > 0 ? percent : null;
  };

  const getBillingPeriod = () => {
    if (isFree) return '';
    if (isYearly) {
      return t('cards_section.card.billed_yearly');
    } else {
      return t('cards_section.card.billed_monthly');
    }
  };

  // Feature configuration based on license type and plan
  const getFeatureConfig = () => {
    const mainFeatures: Array<{
      name: string;
      value: string | number;
      hint?: string;
      included: boolean;
      icon: string;
      counter?: boolean;
      counterId?: string;
      price?: number;
    }> = [];
    const additionalFeatures: Array<{
      name: string;
      value: string | number;
      hint?: string;
      included: boolean;
      icon: string;
      counter?: boolean;
      counterId?: string;
      price?: number;
    }> = [];

    // For mobile app (Seller2Go).
    // Main features mirror the screenshot; additional features are driven strictly
    // by the API options so the check/cross marks reflect each plan correctly.
    if (licenseType === 'mobile') {
      const concurrentUsersCount = userIncludedCount ?? maxUsers ?? 1;

      mainFeatures.push({
        name: t('cards_section.card.features.concurrent_users'),
        // Free shows the explicit "1 main, 1 sub-user" breakdown per the screenshot;
        // higher tiers just show the total count.
        value: mobilePlanTier === 'free'
          ? t('cards_section.card.features.users_main_sub', { count: concurrentUsersCount })
          : String(concurrentUsersCount),
        included: true,
        icon: licenseStarIcon
      });

      mainFeatures.push({
        name: t('cards_section.card.features.domains_shops'),
        value: String(shopIncludedCount ?? 1),
        included: true,
        icon: licenseStarIcon
      });

      // Sync interval label is derived from the API value (seconds).
      let syncIntervalValue: string;
      if (syncIntervalSec === 0) {
        syncIntervalValue = t('cards_section.card.features.sync_interval_instant');
      } else if (syncIntervalSec === 300) {
        syncIntervalValue = t('cards_section.card.features.sync_interval_5_or_10');
      } else if (syncIntervalSec === 600) {
        syncIntervalValue = t('cards_section.card.features.sync_interval_10');
      } else {
        syncIntervalValue = t('cards_section.card.features.coming_soon');
      }
      mainFeatures.push({
        name: t('cards_section.card.features.sync_interval'),
        value: syncIntervalValue,
        included: true,
        icon: licenseStarIcon
      });

      // Mobile notifications stays in the list per product owner request,
      // even though it is not pictured on the latest screenshot.
      mainFeatures.push({
        name: t('cards_section.card.features.mobile_notifications'),
        value: t('cards_section.card.features.mobile_notifications_desc'),
        included: true,
        icon: licenseStarIcon
      });

      additionalFeatures.push({
        name: t('cards_section.card.features.jtl_wawi_integration'),
        value: '',
        included: jtlWawiEnabled,
        icon: jtlWawiEnabled ? licenseCheckIcon : licenseCrossIcon
      });

      additionalFeatures.push({
        name: t('cards_section.card.features.view_shipping_orders'),
        value: shippingOrdersEnabled
          ? t('cards_section.card.features.view_shipping_orders_up_to', {
              count: concurrentUsersCount,
              main: Math.max(concurrentUsersCount - 1, 0)
            })
          : '',
        included: shippingOrdersEnabled,
        icon: shippingOrdersEnabled ? licenseCheckIcon : licenseCrossIcon
      });

      additionalFeatures.push({
        name: t('cards_section.card.features.analytics_dashboard'),
        value: '',
        included: analyticsEnabled,
        icon: analyticsEnabled ? licenseCheckIcon : licenseCrossIcon
      });

      additionalFeatures.push({
        name: t('cards_section.card.features.downtime_alert'),
        value: downtimeAlertEnabled
          ? t('cards_section.card.features.downtime_alert_desc')
          : '',
        included: downtimeAlertEnabled,
        icon: downtimeAlertEnabled ? licenseCheckIcon : licenseCrossIcon
      });
    }

    // For content master - main features mirror the screenshot exactly.
    // The "Wawi attributes & characteristics" item is moved to additionalFeatures
    // because its availability differs per plan (driven by edit_wawi_attributes).
    if (licenseType === 'content_master') {
      if (Number.isFinite(tokenIncludedCount) && tokenIncludedCount > 0) {
        const tokensPerProduct = isTrial
          ? CONTENT_MASTER_TRIAL_TOKENS_PER_PRODUCT
          : CONTENT_MASTER_APPROX_TOKENS_PER_PRODUCT;
        const approxProducts = Math.round(tokenIncludedCount / tokensPerProduct);
        mainFeatures.push({
          name: t('cards_section.card.features.content_master_included_ai_tokens'),
          value: t('cards_section.card.features.content_master_included_ai_tokens_value', {
            products: approxProducts.toLocaleString()
          }),
          included: true,
          icon: licenseStarIcon,
        });
      }
      mainFeatures.push({
        name: t('cards_section.card.features.content_master_ai_descriptions'),
        value: t('cards_section.card.features.content_master_ai_descriptions_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.content_master_seo_meta'),
        value: t('cards_section.card.features.content_master_seo_meta_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.content_master_translations'),
        value: t('cards_section.card.features.content_master_translations_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.content_master_drafts_history'),
        value: t('cards_section.card.features.content_master_drafts_history_desc'),
        included: true,
        icon: licenseStarIcon,
      });

      // "Wawi attributes & characteristics" is included only on plans where
      // the API exposes edit_wawi_attributes=true (Pro, Ultimate).
      additionalFeatures.push({
        name: t('cards_section.card.features.content_master_wawi_attributes'),
        value: editWawiAttributesEnabled
          ? t('cards_section.card.features.content_master_wawi_attributes_desc')
          : '',
        included: editWawiAttributesEnabled,
        icon: editWawiAttributesEnabled ? licenseCheckIcon : licenseCrossIcon,
      });

      additionalFeatures.push({
        name: t('cards_section.card.features.content_master_bulk_generation'),
        value: bulkGenerationIncluded
          ? t('cards_section.card.features.content_master_bulk_generation_desc')
          : '',
        included: bulkGenerationIncluded,
        icon: bulkGenerationIncluded ? licenseCheckIcon : licenseCrossIcon,
      });
    }

    // For freshdesk
    if (licenseType === 'freshdesk') {
      mainFeatures.push({
        name: t('cards_section.card.features.emails'),
        value: t('cards_section.card.features.up_to_emails_month', { count: emailQuotaBase || 1000 }),
        hint: t('cards_section.card.emails_hint'),
        included: true,
        icon: licenseStarIcon,
        counter: false,
        counterId: 'emails'
      });

      mainFeatures.push({
        name: t('cards_section.card.features.users'),
        value: extraUsersIncluded || 1,
        hint: t('cards_section.card.features.users_hint'),
        included: true,
        icon: licenseStarIcon,
        counter: false,
        counterId: 'users'
      });

      // Additional features only for paid plans
      if (!isFree) {
        additionalFeatures.push({
          name: t('cards_section.card.features.additional_emails'),
          value: t('cards_section.card.features.emails_description'),
          hint: t('cards_section.card.emails_hint'),
          price: isYearly ? (emailBlockPriceCents || 0) / 12 : (emailBlockPriceCents || 0),
          included: false,
          counter: true,
          counterId: 'emails',
          icon: licenseCheckIcon
        });

        additionalFeatures.push({
          name: t('cards_section.card.features.extra_freshdesk_users'),
          value: t('cards_section.card.features.users_description'),
          hint: t('cards_section.card.users_hint'),
          price: isYearly ? (extraUserPriceCents || 0) / 12 : (extraUserPriceCents || 0),
          included: false,
          counter: true,
          counterId: 'users',
          icon: licenseCheckIcon
        });
      } else {
        additionalFeatures.push({
          name: t('cards_section.card.features.additional_emails'),
          value: t('cards_section.card.features.emails_description'),
          hint: t('cards_section.card.emails_hint'),
          included: false,
          icon: licenseCrossIcon
        });

        additionalFeatures.push({
          name: t('cards_section.card.features.extra_freshdesk_users'), 
          value: t('cards_section.card.features.users_description'),
          hint: t('cards_section.card.users_hint'),
          included: false,
          icon: licenseCrossIcon
        });
      }
    }

    if (licenseType === 'shipment_tracker') {
      mainFeatures.push({
        name: t('cards_section.card.features.shipment_tracker_unlimited'),
        value: t('cards_section.card.features.shipment_tracker_unlimited_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.shipment_tracker_landed_cost'),
        value: t('cards_section.card.features.shipment_tracker_landed_cost_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.shipment_tracker_visibility'),
        value: t('cards_section.card.features.shipment_tracker_visibility_desc'),
        included: true,
        icon: licenseStarIcon,
      });
    }

    if (licenseType === 'google_review_manager') {
      mainFeatures.push({
        name: t('cards_section.card.features.google_review_ai_replies'),
        value: t('cards_section.card.features.google_review_ai_replies_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.google_review_publish'),
        value: t('cards_section.card.features.google_review_publish_desc'),
        included: true,
        icon: licenseStarIcon,
      });
      mainFeatures.push({
        name: t('cards_section.card.features.google_review_bulk'),
        value: t('cards_section.card.features.google_review_bulk_desc'),
        included: true,
        icon: licenseStarIcon,
      });
    }

    return { mainFeatures, additionalFeatures };
  };

  const { mainFeatures, additionalFeatures } = getFeatureConfig();

  const handleActivateFree = async () => {
    if (!product.pluginName) return;
    
    setIsActivating(true);
    try {
      await SubscriptionService.postAppSubscriptionActivateFree(product.pluginName);
      
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('license_free_activated'),
      }));
      
      // Refresh licenses list
      dispatch(fetchUserLicenses({ page: 1, pageSize: 100 }));
    } catch (error: any) {
      const errorMessage = error?.body?.error || error?.message || t('license_free_activation_failed');
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: errorMessage,
      }));
    } finally {
      setIsActivating(false);
    }
  };

  const handleAction = () => {
    // Don't allow any action if user already has active license or payment is pending
    if (hasActiveLicense || isPaymentPending || isActivating || isChangePlanInProgress) {
      return;
    }

    if (product.pluginName === 'mobile_default') {
      const getNowElement = document.querySelector('.get-now');
      if (getNowElement) {
        getNowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    
    if (product.pluginName === 'freshdesk' && !isFree && !isUserAgreedWithOverage) {
      const cardElement = document.querySelector('.auto-upgrade-checkbox');
      if (cardElement) {
        cardElement.classList.add('shake');
        setTimeout(() => {
          cardElement.classList.remove('shake');
        }, 500);
      }
      return;
    }

    // Activate free license
    if (isFree) {
      handleActivateFree();
      return;
    }

    // Pass additional counts for freshdesk and total price
    const emailCount = licenseType === 'freshdesk' && additionalEmails > 0 ? additionalEmails : undefined;
    const userCount = licenseType === 'freshdesk' && additionalUsers > 0 ? additionalUsers : undefined;
    
    onPurchase(product, emailCount, userCount, getTotalPriceCents());
  };

  const getActionText = () => {
    if (isFree) {
      if (licenseType === 'mobile') {
        return t('cards_section.card.button_text_free_mobile');
      }
      if (licenseType === 'content_master' && isTrial) {
        // Trial end date computed from the product duration, not a hardcoded day count.
        const trialEndDate = new Date(Date.now() + product.durationSec * 1000).toLocaleDateString();
        return t('cards_section.card.button_text_free_trial_until', { date: trialEndDate });
      }
      return t('cards_section.card.button_text_free');
    }
    const price = getCardPrice();
    return `${t('cards_section.card.button_text_select')} ${displayName} | ${price}`;
  };

  const discountPercent = getDiscountPercent();
  const isPromoOffer = promoDiscountPercent !== null;

  const handleCancel = () => {
    if (isFree || !activeLicenseId) {
      return;
    }

    setIsCancelModalOpen(true);
  };

  // Contact-only plans have no online checkout - point the user at the contact
  // form already rendered further down the buy page.
  const handleContactSales = () => {
    const contactForm = document.querySelector('.contact-form');
    if (contactForm) {
      contactForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Freshdesk add-on counts to forward to change-plan: only counters the user
  // explicitly touched are sent; the rest stay `undefined` = keep current.
  const handleChangePlanClick = () => {
    const email = licenseType === 'freshdesk' && emailsTouched ? additionalEmails : undefined;
    const agent = licenseType === 'freshdesk' && usersTouched ? additionalUsers : undefined;
    onChangePlan?.(product, email, agent);
  };

  return (
    <div className={`license-card ${className} ${isYearly ? 'featured' : ''}`}>
      {discountPercent !== null && (
        <div className={`discount-badge${isPromoOffer ? ' discount-badge--promo' : ''}`}>
          <span className="discount-badge-percent">-{discountPercent}%</span>
          {/* {isPromoOffer && (
            <span className="discount-badge-label">
              {t('cards_section.card.promo_badge_label')}
            </span>
          )} */}
        </div>
      )}

      <div className="license-card-content">
        <div className="license-card-header">
          <h3 className="license-card-title">{displayName}</h3>
          <div className="license-card-price">
            {isContactOnly ? (
              <span className="current-price">{t('cards_section.card.contact_for_pricing')}</span>
            ) : (
              <>
                {/* With a validated coupon the crossed-out price is the actual
                    product price (it replaces the yearly/promo original). */}
                {hasCouponDiscount ? (
                  <span className="original-price">
                    {helper.formatPrice({ amount: getDisplayPriceCents(getTotalPriceCents()), currency: 'EUR' })}
                  </span>
                ) : (
                  getOriginalPrice() && (
                    <span className="original-price">{getOriginalPrice()}</span>
                  )
                )}
                <span className="current-price">
                  {getHeaderPrice()}
                  {showMonthlyEquivalent && `/${t('cards_section.card.month')}`}
                </span>
                <span className="billing-label">{getBillingPeriod()}</span>
              </>
            )}
          </div>
        </div>

        {licenseType === 'content_master' && Number.isFinite(tokenIncludedCount) && tokenIncludedCount > 0 && (
          <div className="content-master-credits-box">
            <span className="content-master-credits-text">
              {t(
                isTrial
                  ? 'cards_section.card.features.content_master_credits'
                  : 'cards_section.card.features.content_master_credits_per_month',
                { credits: tokenIncludedCount.toLocaleString() }
              )}
            </span>
            {tokenMultiplier > 1 && (
              <span className="content-master-credits-badge">
                {t('cards_section.card.features.content_master_more_credits', {
                  multiplier: tokenMultiplier
                })}
              </span>
            )}
          </div>
        )}

        <div className="card-line">
          <div className="card-line-item"></div>
        </div>

        <div className="license-card-features">
          <ul className="features-list">
            {mainFeatures.map((feature, index) => (
              <li key={index} className={`feature-item`}>
                <span className="feature-icon">
                  <img src={feature.icon} alt="feature" />
                </span>
                <div className="feature-body">
                  <div className="feature-body-content">
                    <span className="feature-name">{feature.name}</span>
                    {feature.value && (
                      <span className="feature-value">{feature.value}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {additionalFeatures.length > 0 && (
            <div className="card-line">
              <div className={`card-line-item ${isTierUpgradeSection ? 'card-line-bottom-mobile' : 'card-line-bottom'}`}></div>
            </div>
          )}

          {additionalFeatures.length > 0 && (
            <h3 className={`additional-features-title ${isTierUpgradeSection ? 'additional-features-title-mobile' : ''}`}>
              {(() => {
                if (licenseType === 'mobile' && mobilePlanTier === 'pro') {
                  return t('cards_section.card.everything_in_free');
                }
                if (licenseType === 'mobile' && mobilePlanTier === 'ultimate') {
                  return t('cards_section.card.everything_in_pro');
                }
                if (licenseType === 'mobile' && !isFree) {
                  return t('cards_section.card.everything_in_previous');
                }
                if (licenseType === 'content_master' && contentMasterPlanTier === 'pro') {
                  return t('cards_section.card.everything_in_starter');
                }
                if (licenseType === 'content_master' && contentMasterPlanTier === 'ultimate') {
                  return t('cards_section.card.everything_in_pro');
                }
                return t('cards_section.card.additional_features');
              })()}
            </h3>
          )}

          {additionalFeatures.length > 0 && (
          <ul className="features-list additional-features-list">
            {additionalFeatures.map((feature, index) => (
              <li key={index} className={`feature-item`}>
                <span className="feature-icon">
                  <img src={feature.icon} alt="feature" />
                </span>
                <div className="feature-body">
                  <div className="feature-body-content">
                    <span className="feature-name">
                      {feature.name}
                      {feature.hint && (
                        <Hint hintText={feature.hint} />
                      )}
                    </span>
                    {feature.value && (
                      <span className="feature-value">{feature.value}</span>
                    )}
                  </div>
                  {feature.counter && (
                    <div className="feature-counter">
                      <span className="extra-option-price">
                        {helper.formatPrice({ amount: feature.price || 0, currency: 'EUR' })}/{t('cards_section.card.month')}
                      </span>
                      <button 
                        className="counter-btn counter-minus"
                        onClick={() => {
                          if (feature.counterId === 'emails' && additionalEmails > 0) {
                            setAdditionalEmails(additionalEmails - 1);
                            setEmailsTouched(true);
                          } else if (feature.counterId === 'users' && additionalUsers > 0) {
                            setAdditionalUsers(additionalUsers - 1);
                            setUsersTouched(true);
                          }
                        }}
                        disabled={
                          (hasActiveLicense && !allowOptionsChange) ||
                          isPaymentPending ||
                          isChangePlanInProgress ||
                          (feature.counterId === 'emails' && additionalEmails === 0) ||
                          (feature.counterId === 'users' && additionalUsers === 0)
                        }
                      >
                        <span>-</span>
                      </button>
                      <span className="counter-value">
                        {feature.counterId === 'emails' ? additionalEmails : additionalUsers}
                      </span>
                      <button 
                        className="counter-btn counter-plus"
                        onClick={() => {
                          if (feature.counterId === 'emails' && additionalEmails <= 45) {
                            setAdditionalEmails(additionalEmails + 1);
                            setEmailsTouched(true);
                          } else if (feature.counterId === 'users' && additionalUsers <= 45) {
                            setAdditionalUsers(additionalUsers + 1);
                            setUsersTouched(true);
                          }
                        }}
                        disabled={
                          (hasActiveLicense && !allowOptionsChange) ||
                          isPaymentPending ||
                          isChangePlanInProgress ||
                          (feature.counterId === 'emails' && additionalEmails >= 45) ||
                          (feature.counterId === 'users' && additionalUsers >= 45)
                        }
                      >
                        <span>+</span>
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          )}
          
          {licenseType === 'freshdesk' && !isFree && !hasActiveLicense && (
            <div className="additional-features">
              <div className="auto-upgrade-checkbox">
                <input checked={isUserAgreedWithOverage} onChange={() => setIsUserAgreedWithOverage(!isUserAgreedWithOverage)} type="checkbox" id={`auto-upgrade-${product.id}`} />
                <label htmlFor={`auto-upgrade-${product.id}`}>
                  {t('cards_section.card.auto_upgrade')}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {trialExpirationDate && (() => {
        const trialDaysLeft = Math.ceil(
          (new Date(trialExpirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        return (
          <div
            className={`license-card-trial-note${trialDaysLeft <= 3 ? ' license-card-trial-note--warning' : ''}`}
          >
            {t('subscription_trial_banner', { date: helper.formatDateOnly(trialExpirationDate) })}
          </div>
        );
      })()}

      {hasActiveLicense && isCancelled && expirationDate && (
        <button className="license-card-cancelled-info license-card-button free" disabled={true}>
          <span>{t('cards_section.card.cancelled_valid_until', { date: new Date(expirationDate).toLocaleDateString() })}</span>
        </button>
      )}

      {hasActiveLicense && !isCancelled && pendingScheduledProductName && (
        <div className="license-card-pending-change">
          <span className="license-card-pending-change-badge">
            {t('subscription_pending_change_badge', {
              date: expirationDate ? new Date(expirationDate).toLocaleDateString() : '-',
              newPlan: pendingScheduledProductName,
            })}
          </span>
          {!isSubuser && (
            <button
              type="button"
              className="license-card-pending-change-cancel"
              onClick={() => setIsCancelScheduledModalOpen(true)}
            >
              {t('subscription_pending_change_cancel_button')}
            </button>
          )}
        </div>
      )}

      {hasActiveLicense && !isCancelled && !isSubuser && allowOptionsChange && (
        <button
          className="license-card-button premium"
          onClick={handleChangePlanClick}
          disabled={isPaymentPending || isChangePlanInProgress}
        >
          {t('subscription_change_options_button')}
        </button>
      )}

      {hasActiveLicense && !isCancelled && !isSubuser && (
        <button
          className="license-card-button cancel"
          disabled={isFree}
          onClick={handleCancel}
        >
          {!isFree &&
            <span>{t('cards_section.card.button_text_cancel')}</span>
          }
          {isFree &&
            <span>{t('cards_section.card.not_available')}</span>
          }
        </button>
      )}

      {!hasActiveLicense && pluginCanChangePlan && !isFree && !isSubuser && (
        <button
          className="license-card-button premium"
          onClick={handleChangePlanClick}
          disabled={isPaymentPending || isChangePlanInProgress}
        >
          {t('subscription_change_button')}
        </button>
      )}

      {!hasActiveLicense && isContactOnly && (
        <button
          className="license-card-button premium"
          onClick={handleContactSales}
          type="button"
        >
          {t('cards_section.card.button_text_contact_sales')}
        </button>
      )}

      {!hasActiveLicense && !isContactOnly && !(pluginCanChangePlan && !isFree && !isSubuser) && (
        <button
          className={`license-card-button ${isFree ? 'free' : 'premium'}`}
          onClick={handleAction}
          disabled={isPaymentPending || isActivating || isChangePlanInProgress}
        >
          {isPaymentPending ? t('license_payment_processing') : 
           isActivating ? t('license_activating') : getActionText()}
          {licenseType === 'mobile' && isFree && !isPaymentPending && !isActivating && (
            <Hint hintText={t('cards_section.card.free_mobile_button_hint')} />
          )}
        </button>
      )}

      {/* Cancel License Modal */}
      {activeLicenseId && (
        <LicenseCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          licenseId={activeLicenseId}
          licenseName={product.name}
          licenseType={licenseType}
          expirationDate={expirationDate}
          hasPendingScheduledChange={!!pendingScheduledProductName}
        />
      )}

      {/* Cancel Scheduled Change Modal */}
      {activeLicenseId && pendingScheduledProductName && (
        <LicenseCancelScheduledChangeModal
          isOpen={isCancelScheduledModalOpen}
          onClose={() => setIsCancelScheduledModalOpen(false)}
          subscriptionId={activeLicenseId}
          currentProductName={product.name}
          scheduledProductName={pendingScheduledProductName}
        />
      )}
    </div>
  );
};

export default LicenseBuyCard;
